/**
 * A text field's own format: a `pattern` (a safe subset of regular expressions) and/or a `mask` (`000.000-00`: `0` a
 * digit, `A` a letter, `*` either, `\` makes the next character literal, anything else a separator). The same engine
 * as the server's (server/src/formats/custom.rs; `vectors.json` holds cases both are tested against): patterns match
 * the whole value, compiled to an NFA and simulated (a Pike VM), never backtracked, so no pattern is catastrophic and
 * nothing depends on the browser's RegExp. What's refused there is refused here.
 */

const MAX_PATTERN = 200;
const MAX_MASK = 100;
const MAX_REPEAT = 100;
const MAX_PROGRAM = 2000;

type Range = readonly [string, string];

type Node =
  | { t: "char"; c: string }
  | { t: "any" }
  | { t: "class"; ranges: Range[]; neg: boolean }
  | { t: "cat"; items: Node[] }
  | { t: "alt"; branches: Node[] }
  | { t: "repeat"; atom: Node; min: number; max: number | null };

const DIGIT: Range[] = [["0", "9"]];
const WORD: Range[] = [
  ["0", "9"],
  ["A", "Z"],
  ["_", "_"],
  ["a", "z"],
];
const SPACE: Range[] = [
  ["\t", "\r"],
  [" ", " "],
];
/** What `\` may make literal. */
const PUNCT = "\\.^$|?*+()[]{}-/";
const ASCII_DIGIT = /^[0-9]$/u;
const LETTER = /^\p{Alphabetic}$/u;
const LETTER_OR_DIGIT = /[\p{Alphabetic}\p{N}]/u;

/** Thrown inside the parser for anything outside the subset. */
class Refused extends Error {}

const refuse = (): never => {
  throw new Refused();
};

class Parser {
  at = 0;
  readonly s: readonly string[];
  constructor(s: readonly string[]) {
    this.s = s;
  }

  peek(): string | undefined {
    return this.s[this.at];
  }

  next(): string | undefined {
    const c = this.peek();
    if (c !== undefined) {
      this.at += 1;
    }
    return c;
  }

  alt(): Node {
    const branches = [this.cat()];
    while (this.peek() === "|") {
      this.at += 1;
      branches.push(this.cat());
    }
    return branches.length === 1 ? (branches[0] as Node) : { t: "alt", branches };
  }

  cat(): Node {
    const items: Node[] = [];
    for (let c = this.peek(); c !== undefined && c !== "|" && c !== ")"; c = this.peek()) {
      items.push(this.quantified(this.atom()));
    }
    return { t: "cat", items };
  }

  /** `atom` with the quantifier after it, if any (one: another after it is refused). */
  quantified(atom: Node): Node {
    let min: number;
    let max: number | null;
    switch (this.peek()) {
      case "?":
        [min, max] = [0, 1];
        break;
      case "*":
        [min, max] = [0, null];
        break;
      case "+":
        [min, max] = [1, null];
        break;
      case "{": {
        this.at += 1;
        min = this.number();
        const c = this.next();
        if (c === "}") {
          max = min;
        } else if (c === "," && this.peek() === "}") {
          this.at += 1;
          max = null;
        } else if (c === ",") {
          max = this.number();
          if (this.next() !== "}" || max < min) {
            refuse();
          }
        } else {
          return refuse();
        }
        this.at -= 1;
        break;
      }
      default:
        return atom;
    }
    this.at += 1;
    const after = this.peek();
    if (after === "?" || after === "*" || after === "+" || after === "{") {
      refuse();
    }
    return { t: "repeat", atom, min, max };
  }

  number(): number {
    const from = this.at;
    while (ASCII_DIGIT.test(this.peek() ?? "")) {
      this.at += 1;
    }
    const digits = this.s.slice(from, this.at).join("");
    const n = digits === "" ? Number.NaN : Number(digits);
    return Number.isInteger(n) && n <= MAX_REPEAT ? n : refuse();
  }

  atom(): Node {
    const c = this.next() ?? refuse();
    switch (c) {
      case "(": {
        if (this.peek() === "?") {
          this.at += 1;
          if (this.next() !== ":") {
            refuse();
          }
        }
        const inner = this.alt();
        if (this.next() !== ")") {
          refuse();
        }
        return inner;
      }
      case "[":
        return this.klass();
      case ".":
        return { t: "any" };
      case "\\":
        return this.escape();
      case "?":
      case "*":
      case "+":
      case "{":
      case "}":
      case "]":
      case "^":
      case "$":
        return refuse();
      default:
        return { t: "char", c };
    }
  }

  escape(): Node {
    const c = this.next() ?? refuse();
    const shorthand: Record<string, [Range[], boolean]> = {
      d: [DIGIT, false],
      D: [DIGIT, true],
      w: [WORD, false],
      W: [WORD, true],
      s: [SPACE, false],
      S: [SPACE, true],
    };
    const s = shorthand[c];
    if (s) {
      return { t: "class", ranges: [...s[0]], neg: s[1] };
    }
    return PUNCT.includes(c) ? { t: "char", c } : refuse();
  }

  /** One character inside a class (escaped, or itself), or a shorthand's ranges. */
  classChar(): string | Range[] {
    const c = this.next() ?? refuse();
    if (c === "\\") {
      const e = this.next() ?? refuse();
      const shorthand: Record<string, Range[]> = { d: DIGIT, w: WORD, s: SPACE };
      return shorthand[e] ?? (PUNCT.includes(e) ? e : refuse());
    }
    return c === "[" ? refuse() : c;
  }

  klass(): Node {
    const neg = this.peek() === "^";
    if (neg) {
      this.at += 1;
    }
    const ranges: Range[] = [];
    let first = true;
    for (;;) {
      const c = this.peek();
      if (c === undefined) {
        refuse();
      }
      if (c === "]" && !first) {
        this.at += 1;
        break;
      }
      first = false;
      const lo = this.classChar();
      if (typeof lo !== "string") {
        ranges.push(...lo);
        continue;
      }
      const next = this.s[this.at + 1];
      if (this.peek() === "-" && next !== undefined && next !== "]") {
        this.at += 1;
        const hi = this.classChar();
        if (typeof hi !== "string" || (hi.codePointAt(0) ?? 0) < (lo.codePointAt(0) ?? 0)) {
          refuse();
        }
        ranges.push([lo, hi as string]);
      } else {
        ranges.push([lo, lo]);
      }
    }
    return { t: "class", ranges, neg };
  }
}

type Inst =
  | { op: "char"; c: string }
  | { op: "any" }
  | { op: "class"; ranges: Range[]; neg: boolean }
  | { op: "split"; a: number; b: number }
  | { op: "jump"; a: number }
  | { op: "match" };

function emit(n: Node, prog: Inst[]): void {
  if (prog.length > MAX_PROGRAM) {
    refuse();
  }
  switch (n.t) {
    case "char":
      prog.push({ op: "char", c: n.c });
      break;
    case "any":
      prog.push({ op: "any" });
      break;
    case "class":
      prog.push({ op: "class", ranges: n.ranges, neg: n.neg });
      break;
    case "cat":
      for (const i of n.items) {
        emit(i, prog);
      }
      break;
    case "alt": {
      const jumps: number[] = [];
      for (const [i, b] of n.branches.entries()) {
        if (i + 1 < n.branches.length) {
          const split = prog.length;
          prog.push({ op: "split", a: split + 1, b: 0 });
          emit(b, prog);
          jumps.push(prog.length);
          prog.push({ op: "jump", a: 0 });
          prog[split] = { op: "split", a: split + 1, b: prog.length };
        } else {
          emit(b, prog);
        }
      }
      for (const j of jumps) {
        prog[j] = { op: "jump", a: prog.length };
      }
      break;
    }
    case "repeat": {
      for (let i = 0; i < n.min; i += 1) {
        emit(n.atom, prog);
      }
      if (n.max === null) {
        const split = prog.length;
        prog.push({ op: "split", a: split + 1, b: 0 });
        emit(n.atom, prog);
        prog.push({ op: "jump", a: split });
        prog[split] = { op: "split", a: split + 1, b: prog.length };
      } else {
        const splits: number[] = [];
        for (let i = n.min; i < n.max; i += 1) {
          splits.push(prog.length);
          prog.push({ op: "split", a: 0, b: 0 });
          emit(n.atom, prog);
        }
        for (const s of splits) {
          prog[s] = { op: "split", a: s + 1, b: prog.length };
        }
      }
      break;
    }
    default:
      break;
  }
  if (prog.length > MAX_PROGRAM) {
    refuse();
  }
}

/** A compiled pattern. */
export class Pattern {
  private readonly prog: readonly Inst[];
  private constructor(prog: readonly Inst[]) {
    this.prog = prog;
  }

  /** Compiles `p`; null when it's outside the subset or too big. */
  static of(p: string): Pattern | null {
    const chars = [...p];
    if (chars.length === 0 || chars.length > MAX_PATTERN) {
      return null;
    }
    let body = p.startsWith("^") ? p.slice(1) : p;
    body = body.endsWith("$") && !body.endsWith("\\$") ? body.slice(0, -1) : body;
    try {
      const parser = new Parser([...body]);
      const tree = parser.alt();
      if (parser.at !== parser.s.length) {
        return null;
      }
      const prog: Inst[] = [];
      emit(tree, prog);
      prog.push({ op: "match" });
      return new Pattern(prog);
    } catch (e) {
      if (e instanceof Refused) {
        return null;
      }
      throw e;
    }
  }

  /** Adds `pc` and what it leads to without reading to `list`, once each per step. */
  private add(list: number[], seen: boolean[], start: number): void {
    const stack = [start];
    for (let pc = stack.pop(); pc !== undefined; pc = stack.pop()) {
      if (seen[pc]) {
        continue;
      }
      seen[pc] = true;
      const inst = this.prog[pc];
      if (inst?.op === "split") {
        stack.push(inst.b, inst.a);
      } else if (inst?.op === "jump") {
        stack.push(inst.a);
      } else {
        list.push(pc);
      }
    }
  }

  /** Whether all of `s` matches. */
  matches(s: string): boolean {
    const n = this.prog.length;
    let now: number[] = [];
    this.add(now, new Array<boolean>(n).fill(false), 0);
    for (const c of s) {
      const next: number[] = [];
      const seen = new Array<boolean>(n).fill(false);
      const code = c.codePointAt(0) ?? 0;
      for (const pc of now) {
        const inst = this.prog[pc];
        const takes =
          inst?.op === "char"
            ? inst.c === c
            : inst?.op === "any"
              ? true
              : inst?.op === "class"
                ? inst.ranges.some(
                    ([lo, hi]) => (lo.codePointAt(0) ?? 0) <= code && code <= (hi.codePointAt(0) ?? 0),
                  ) !== inst.neg
                : false;
        if (takes) {
          this.add(next, seen, pc + 1);
        }
      }
      if (next.length === 0) {
        return false;
      }
      now = next;
    }
    return now.some((pc) => this.prog[pc]?.op === "match");
  }
}

type Slot = "digit" | "letter" | "either" | { literal: string };

function parseMask(m: string): Slot[] | null {
  const chars = [...m];
  if (chars.length > MAX_MASK) {
    return null;
  }
  const out: Slot[] = [];
  for (let i = 0; i < chars.length; i += 1) {
    const c = chars[i] as string;
    if (c === "0") {
      out.push("digit");
    } else if (c === "A") {
      out.push("letter");
    } else if (c === "*") {
      out.push("either");
    } else if (c === "\\") {
      i += 1;
      const lit = chars[i];
      if (lit === undefined) {
        return null;
      }
      out.push({ literal: lit });
    } else {
      out.push({ literal: c });
    }
  }
  return out.some((s) => typeof s === "string") ? out : null;
}

/** What a custom-format value compares by: its letters and digits, uppercased. */
export const customKey = (s: string): string =>
  [...s]
    .filter((c) => LETTER_OR_DIGIT.test(c))
    .map((c) => c.toUpperCase())
    .join("");

const fits = (slot: Slot, c: string): boolean =>
  slot === "digit" ? ASCII_DIGIT.test(c) : slot === "letter" ? LETTER.test(c) : true;

/** A field's own format, compiled. */
export class Custom {
  private readonly pattern: Pattern | null;
  private readonly mask: Slot[] | null;
  private constructor(pattern: Pattern | null, mask: Slot[] | null) {
    this.pattern = pattern;
    this.mask = mask;
  }

  /** Compiles a pattern and a mask (at least one); `error` names which is refused. */
  static of(pattern?: string | null, mask?: string | null): Custom | { error: "pattern" | "mask" } {
    const [ps, ms] = [pattern ?? null, mask ?? null];
    if (ps === null && ms === null) {
      return { error: "pattern" };
    }
    const p = ps === null ? null : Pattern.of(ps);
    if (ps !== null && !p) {
      return { error: "pattern" };
    }
    const m = ms === null ? null : parseMask(ms);
    if (ms !== null && !m) {
      return { error: "mask" };
    }
    return new Custom(p, m);
  }

  /** `s`'s letters and digits in the mask's slots (see the server's `fill`): what it fills and whether all went in. */
  private fill(s: string, partial: boolean): [string, boolean] | null {
    if (!this.mask) {
      return null;
    }
    const raw = [...customKey(s)];
    let i = 0;
    let out = "";
    for (const slot of this.mask) {
      if (partial && i >= raw.length) {
        return [out, true];
      }
      if (typeof slot !== "string") {
        const c = slot.literal;
        if (LETTER_OR_DIGIT.test(c) && raw[i] !== undefined && c.toUpperCase() === raw[i]) {
          i += 1;
        }
        out += c;
        continue;
      }
      const c = raw[i];
      if (c === undefined || !fits(slot, c)) {
        return null;
      }
      out += c;
      i += 1;
    }
    return [out, i === raw.length];
  }

  /** Reads `s` (trimmed): valid when it fills the mask and the pattern matches it as written or as the mask shows it. */
  read(value: string): { key: string; display: string; valid: boolean } {
    const s = value.trim();
    const filled = this.fill(s, false);
    const shown = filled?.[1] ? filled[0] : null;
    const fitsMask = !this.mask || shown !== null;
    const matches = !this.pattern || this.pattern.matches(s) || (shown !== null && this.pattern.matches(shown));
    return { key: customKey(s), display: shown ?? s, valid: fitsMask && matches };
  }

  /** What typing `s` shows: its letters and digits with the mask's separators put in as far as they reach. */
  typed(s: string): string {
    const filled = this.fill(s, true);
    return filled?.[1] ? filled[0] : s;
  }

  /** The mask as an example (a placeholder). */
  get placeholder(): string | undefined {
    if (!this.mask) {
      return undefined;
    }
    return this.mask
      .map((s) => (typeof s === "string" ? { digit: "0", letter: "A", either: "*" }[s] : s.literal))
      .join("");
  }

  /** Whether it has a mask (a form of its own to normalize to). */
  get masked(): boolean {
    return this.mask !== null;
  }
}

const compiled = new Map<string, Custom | null>();

/** The compiled spec of a field's `pattern` and `mask` (null when it has neither, or they don't compile); kept. */
export function customOf(field: { pattern?: string | null; mask?: string | null }): Custom | null {
  if ((field.pattern ?? field.mask ?? null) === null) {
    return null;
  }
  const k = `${field.pattern ?? "\u0000"}\u0001${field.mask ?? "\u0000"}`;
  if (!compiled.has(k)) {
    if (compiled.size > 512) {
      compiled.clear();
    }
    const c = Custom.of(field.pattern, field.mask);
    compiled.set(k, c instanceof Custom ? c : null);
  }
  return compiled.get(k) ?? null;
}
